// The QR reader for Scan (José, 23 Oct 2026: "A Scan button in GrailFind scans one sticker after another"). The
// phone's own BarcodeDetector where it reads QR codes; elsewhere (iPhone Safari) the site's copy of the
// barcode-detector ponyfill (public/scan, MIT, zxing-wasm inside), loaded from this site only. It gets ready as the page
// opens (José: "did not immediately recognize"): on iPhone its WebAssembly loads on the first read, so a read of one
// blank pixel does that before Start camera.

export type Detector = { detect(source: CanvasImageSource | ImageBitmap | ImageData): Promise<{ rawValue: string }[]> };

type Ponyfill = {
  setZXingModuleOverrides(o: { locateFile(path: string): string }): void;
  BarcodeDetector: new (o: { formats: string[] }) => Detector;
};

declare global {
  interface Window {
    BarcodeDetector?: (new (o: { formats: string[] }) => Detector) & { getSupportedFormats?(): Promise<string[]> };
    BarcodeDetectionAPI?: Ponyfill;
  }
}

let pending: Promise<Detector> | null = null;

function loadPonyfill(): Promise<Ponyfill> {
  if (window.BarcodeDetectionAPI) return Promise.resolve(window.BarcodeDetectionAPI);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = '/scan/barcode-detector.js';
    s.onload = () => (window.BarcodeDetectionAPI ? resolve(window.BarcodeDetectionAPI) : reject(new Error('no reader')));
    s.onerror = () => reject(new Error('no reader'));
    document.head.appendChild(s);
  });
}

/** One reader however often it is asked for; a failure lets the next ask try again. */
export function makeDetector(): Promise<Detector> {
  if (pending) return pending;
  const own = window.BarcodeDetector;
  const check = own?.getSupportedFormats ? own.getSupportedFormats() : Promise.resolve([] as string[]);
  pending = check
    .then(async (formats) => {
      if (own && formats.includes('qr_code')) return new own({ formats: ['qr_code'] });
      const api = await loadPonyfill();
      api.setZXingModuleOverrides({ locateFile: (path) => `/scan/${path}` });
      return new api.BarcodeDetector({ formats: ['qr_code'] });
    })
    .catch((e) => {
      pending = null;
      throw e;
    });
  return pending;
}

/** Gets the reader ready now: on iPhone this loads its WebAssembly. Asks for no camera. */
export function warmUp() {
  makeDetector()
    .then((d) => d.detect(new ImageData(1, 1)))
    .catch(() => {});
}

/** A sticker's QR is its card page's address: /i/ and the card's 26-letter id. */
export function idOf(text: string | null | undefined): string | null {
  const m = /\/i\/([a-z2-7]{26})(?![a-z2-7])/i.exec(text || '');
  return m ? m[1]!.toLowerCase() : null;
}

/**
 * What the reader looks at, for a picture `vw` x `vh` shown in a box `boxW` x `boxH` (cover): the middle square of
 * what shows, 1.3 times the yellow square (56% of the shown width), no taller than what shows, at most 720 px across.
 * [sx, sy, side, out]: draw the square at (sx, sy) of `side` into `out` x `out`.
 */
export function cropOf(vw: number, vh: number, boxW: number, boxH: number): [number, number, number, number] {
  const box = boxW / boxH || 4 / 3;
  const shownW = Math.min(vw, vh * box);
  const shownH = Math.min(vh, vw / box);
  const side = Math.round(Math.min(shownH, shownW * 0.56 * 1.3));
  return [(vw - side) / 2, (vh - side) / 2, side, Math.min(720, side)];
}
